import React, { useState, useEffect, FormEvent } from 'react';
import { Helmet } from 'react-helmet';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { User } from "lucide-react";
import { useAuth } from "@/hooks/useAuthApi";
import { useOrganization } from "@/hooks/useOrganization";
import { authService, storageService } from "@/lib/api";
import { UserData } from '@/types/user';
import { COMMON_TIMEZONES } from '@/constants/timezones';
import { toast } from "@/hooks/use-toast";
import { HeaderActions } from '@/components/layout/HeaderActions';
import { Breadcrumb, BreadcrumbItem, BreadcrumbList, BreadcrumbPage } from '@/components/ui/breadcrumb';

const AccountSettingsPage = () => {
  const { user, userData, loading: authLoading, refreshUserData } = useAuth();
  const { currentOrganization } = useOrganization();

  const [fullName, setFullName] = useState('');
  const [timezone, setTimezone] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [profilePicFile, setProfilePicFile] = useState<File | null>(null);
  const [profilePicPreview, setProfilePicPreview] = useState<string | null>(null);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  useEffect(() => {
    if (userData) {
      setFullName(userData.displayName || '');
      setTimezone(userData.timezone || '');
      setProfilePicPreview(userData.photoURL || null);
    }
  }, [userData]);

  const handleProfilePicChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      setProfilePicFile(file);
      setProfilePicPreview(URL.createObjectURL(file));
    }
  };

  const handleSaveProfile = async (event: FormEvent) => {
    event.preventDefault();
    if (!user) {
      toast({ title: "Error", description: "You must be logged in to save changes.", variant: "destructive" });
      return;
    }
    setIsSavingProfile(true);

    let newPhotoURL = userData?.photoURL || null;

    try {
      if (profilePicFile) {
        if (!currentOrganization?.id) {
          throw new Error('No organization selected');
        }
        const uploadResponse = await storageService.uploadFile(currentOrganization.id, 'profile_pictures', profilePicFile);
        newPhotoURL = uploadResponse.url;
      }

      const updates: { displayName?: string; photoURL?: string | null } = {};
      if (fullName !== (userData?.displayName || '')) {
        updates.displayName = fullName;
      }
      if (newPhotoURL !== (userData?.photoURL || null)) {
        updates.photoURL = newPhotoURL;
      }

      // Check for timezone changes
      const timezoneChanged = timezone !== (userData?.timezone || '');

      if (Object.keys(updates).length > 0 || timezoneChanged) {
        // Prepare API update data
        const apiUpdates: Partial<UserData> = {};
        if (updates.displayName !== undefined) {
          apiUpdates.display_name = updates.displayName;
        }
        if (updates.photoURL !== undefined) {
          apiUpdates.photo_url = updates.photoURL;
        }
        if (timezoneChanged) {
          apiUpdates.timezone = timezone;
        }

        // Update user profile through API
        if (Object.keys(apiUpdates).length > 0) {
          const updateResponse = await authService.updateUser(apiUpdates);
          if (!updateResponse.success) {
            throw new Error(updateResponse.error || 'Failed to update profile');
          }
        }

        await refreshUserData(); // Refresh user data from API
        toast({ title: "Success", description: "Profile updated successfully." });
      } else {
        toast({ title: "No Changes", description: "No changes to save." });
      }
    } catch (error) {
      console.error("Error saving profile:", error);
      toast({ title: "Error", description: "Failed to save profile. Please try again.", variant: "destructive" });
    } finally {
      setIsSavingProfile(false);
      setProfilePicFile(null); // Reset file input state
    }
  };

  const handleChangePassword = async (event: FormEvent) => {
    event.preventDefault();
    if (!user || !user.email) {
      toast({ title: "Error", description: "User not found or email missing.", variant: "destructive" });
      return;
    }
    if (newPassword !== confirmPassword) {
      toast({ title: "Error", description: "New passwords do not match.", variant: "destructive" });
      return;
    }
    if (!newPassword) {
      toast({ title: "Error", description: "New password cannot be empty.", variant: "destructive" });
      return;
    }

    setIsChangingPassword(true);
    try {
      const changePasswordResponse = await authService.changePassword({
        current_password: currentPassword,
        new_password: newPassword
      });
      
      if (!changePasswordResponse.success) {
        throw new Error(changePasswordResponse.error || 'Failed to change password');
      }
      
      toast({ title: "Success", description: "Password changed successfully." });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      console.error("Error changing password:", error);
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setIsChangingPassword(false);
    }
  };

  if (authLoading) {
    return (
        <div className="flex min-h-0 flex-col">
          <div className="px-4 sm:px-6 md:px-8 pt-2 pb-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <Skeleton className="h-6 w-48" />
              </div>
            </div>
          </div>
          <div className="flex-1 min-w-0 px-4 sm:px-6 md:px-8 py-6">
            <Skeleton className="h-48 w-full mb-6" />
            <Skeleton className="h-64 w-full" />
          </div>
        </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Account Settings | Hobbes</title>
      </Helmet>
      <div className="flex min-h-0 flex-col">
          {/* Top Navigation Bar */}
          <div className="px-4 sm:px-6 md:px-8 pt-2 pb-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <Breadcrumb>
                  <BreadcrumbList>
                    <BreadcrumbItem>
                      <BreadcrumbPage className="flex items-center gap-2">
                        <User className="h-5 w-5 text-icon" />
                        <span className="text-xl font-semibold text-subheading">Account Settings</span>
                    </BreadcrumbPage>
                  </BreadcrumbItem>
                </BreadcrumbList>
              </Breadcrumb>
            </div>
            <HeaderActions />
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 min-w-0 px-4 sm:px-6 md:px-8 py-6">
          <form onSubmit={handleSaveProfile}>
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>Profile Information</CardTitle>
                <CardDescription>Manage your personal information and profile picture.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex flex-col items-center space-y-2">
                  <Avatar className="h-24 w-24">
                    <AvatarImage src={profilePicPreview || undefined} alt={userData?.displayName || "User Avatar"} />
                    <AvatarFallback>
                      {userData?.displayName?.charAt(0).toUpperCase() || userData?.email?.charAt(0).toUpperCase() || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <Input 
                    id="profilePic" 
                    type="file" 
                    accept="image/*" 
                    onChange={handleProfilePicChange} 
                    className="max-w-xs"
                    disabled={isSavingProfile}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="name">Full Name</Label>
                  <Input 
                    id="name" 
                    value={fullName} 
                    onChange={(e) => setFullName(e.target.value)} 
                    disabled={isSavingProfile}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="email">Email Address</Label>
                  <Input id="email" type="email" value={userData?.email || ''} disabled />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="timezone">Timezone</Label>
                  <Select value={timezone} onValueChange={setTimezone} disabled={isSavingProfile}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select your timezone" />
                    </SelectTrigger>
                    <SelectContent>
                      {COMMON_TIMEZONES.map((tz) => (
                        <SelectItem key={tz.value} value={tz.value}>
                          {tz.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button type="submit" disabled={isSavingProfile}>
                  {isSavingProfile ? 'Saving...' : 'Save Profile Changes'}
                </Button>
              </CardContent>
            </Card>
          </form>

          <Separator className="my-8" />

          <form onSubmit={handleChangePassword}>
            <Card>
              <CardHeader>
                <CardTitle>Password Management</CardTitle>
                <CardDescription>Change your account password.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-1">
                  <Label htmlFor="currentPassword">Current Password</Label>
                  <Input 
                    id="currentPassword" 
                    type="password" 
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    disabled={isChangingPassword}
                    required
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="newPassword">New Password</Label>
                  <Input 
                    id="newPassword" 
                    type="password" 
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    disabled={isChangingPassword}
                    required
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="confirmPassword">Confirm New Password</Label>
                  <Input 
                    id="confirmPassword" 
                    type="password" 
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={isChangingPassword}
                    required
                  />
                </div>
                <Button type="submit" disabled={isChangingPassword}>
                  {isChangingPassword ? 'Changing...' : 'Change Password'}
                </Button>
              </CardContent>
            </Card>
          </form>
        </div>
      </div>
    </>
  );
};

export default AccountSettingsPage;
